/**
 * Gets the support email address from environment variables
 */
export function getSupportEmail(): string {
  return process.env.NEXT_PUBLIC_BUSINESS_EMAIL || "support@build-a-dress.com";
}

/**
 * Parses rate limit error messages and returns user-friendly messages
 *
 * Handles Supabase Auth rate limit errors like:
 * - "For security purposes, you can only request this after 26 seconds"
 * - Similar rate limit patterns
 *
 * @param errorMessage - The raw error message from the API
 * @param includeSupportEmail - Whether to include support email in the message
 * @returns User-friendly error message
 */
export function parseRateLimitError(
  errorMessage: string,
  includeSupportEmail: boolean = false
): string {
  if (!errorMessage) {
    return errorMessage;
  }

  // Check if this is a rate limit error
  const isRateLimitError =
    errorMessage.toLowerCase().includes("for security purposes") ||
    errorMessage.toLowerCase().includes("rate limit") ||
    errorMessage.toLowerCase().includes("too many requests") ||
    /after \d+ seconds?/i.test(errorMessage);

  if (!isRateLimitError) {
    return errorMessage;
  }

  const supportEmail = includeSupportEmail ? getSupportEmail() : null;
  const supportText = supportEmail
    ? ` If this keeps happening, contact us at ${supportEmail}.`
    : "";

  // Extract time value from error message (e.g., "26 seconds" -> 26)
  const timeMatch = errorMessage.match(/(\d+)\s*seconds?/i);
  if (timeMatch && timeMatch[1]) {
    const seconds = parseInt(timeMatch[1], 10);
    // Round up to nearest 5 seconds for user-friendly display
    const roundedSeconds = Math.ceil(seconds / 5) * 5;
    return `Please wait ${roundedSeconds} seconds before trying again.${supportText}`;
  }

  // Fallback for rate limit errors without specific time
  return `Please wait a moment before trying again.${supportText}`;
}
